/**
 * 小程序 web-view 桥接工具。
 *
 * 当 H5 被嵌入微信小程序的 <web-view> 时，小程序会通过 URL query 参数
 * 传递 access_token 和 refresh_token，H5 读取后写入 localStorage 并设置
 * 内存中的 access_token，从而复用 H5 原有的认证流程。
 *
 * 安全性考虑：
 * - URL 中的 token 会在浏览器历史记录中留下痕迹，但 web-view 环境下用户
 *   无法直接查看/编辑 URL，风险可控。
 * - 读取后应立即从 URL 中清除 token 参数（使用 history.replaceState），
 *   避免后续分享或复制链接时泄露。
 */

import { setAccessToken, writePersistedRefresh } from "@/api/client";

const QUERY_ACCESS_TOKEN = "access_token";
const QUERY_REFRESH_TOKEN = "refresh_token";

/** 微信小程序 web-view 注入的 wx.miniProgram 对象类型 */
interface WxMiniProgram {
  navigateTo: (options: { url: string }) => void;
  reLaunch: (options: { url: string }) => void;
  postMessage: (options: { data: Record<string, unknown> }) => void;
}

// 缓存"是否来自小程序"的判断结果，避免 URL 被清除后丢失信号
let _isFromMiniProgram = false;

/**
 * 获取小程序 web-view 注入的 wx.miniProgram 对象（如存在）
 *
 * 注意：window.wx 的类型由 useWechatShare.ts 中的 WxSdk 声明，
 * 但该类型未包含小程序环境注入的 miniProgram 字段，因此这里通过
 * 类型断言访问。
 */
function getWxMiniProgram(): WxMiniProgram | undefined {
  if (typeof window === "undefined") return undefined;
  const wx = (window as unknown as { wx?: { miniProgram?: WxMiniProgram } }).wx;
  return wx?.miniProgram;
}

/**
 * 检测当前是否运行在微信小程序 web-view 中
 */
export function isInMiniProgram(): boolean {
  if (_isFromMiniProgram) return true;
  const detected = !!getWxMiniProgram();
  if (detected) {
    _isFromMiniProgram = true;
  }
  return detected;
}

/**
 * 从 URL query 中读取小程序传递的 token，写入 localStorage 和内存。
 * 读取后清除 URL 中的 token 参数。
 *
 * @returns 是否读取到了 token（true 表示来自小程序的桥接登录）
 */
export function consumeMpTokens(): boolean {
  if (typeof window === "undefined") return false;

  const params = new URLSearchParams(window.location.search);
  const accessToken = params.get(QUERY_ACCESS_TOKEN);
  const refreshToken = params.get(QUERY_REFRESH_TOKEN);

  if (!accessToken && !refreshToken) return false;

  // 写入内存和 localStorage
  if (accessToken) {
    setAccessToken(accessToken);
  }
  if (refreshToken) {
    writePersistedRefresh(refreshToken);
  }

  // 标记来自小程序（URL 清除后仍能识别）
  _isFromMiniProgram = true;

  // 从 URL 中清除 token 参数，避免泄露
  params.delete(QUERY_ACCESS_TOKEN);
  params.delete(QUERY_REFRESH_TOKEN);
  const remaining = params.toString();
  const newSearch = remaining ? `?${remaining}` : "";
  const newUrl = `${window.location.pathname}${newSearch}${window.location.hash}`;
  window.history.replaceState({}, "", newUrl);

  console.log("[mp-bridge] consumed tokens from URL query");
  return true;
}

/**
 * 通知小程序 token 已更新（H5 刷新 token 后调用）。
 * 注意：wx.miniProgram.postMessage 的消息不会实时到达小程序，
 * 只在小程序后退、组件销毁、分享、复制链接时触发。
 */
export function notifyTokensUpdated(accessToken: string, refreshToken: string): void {
  const mp = getWxMiniProgram();
  if (!mp) return;
  mp.postMessage({
    data: {
      type: "tokens_updated",
      access_token: accessToken,
      refresh_token: refreshToken,
    },
  });
}

/**
 * 通知小程序用户已登出。
 */
export function notifyLogout(): void {
  const mp = getWxMiniProgram();
  if (!mp) return;
  mp.postMessage({
    data: {
      type: "logout",
    },
  });
}

/**
 * 跳转到小程序原生页面（通过 wx.miniProgram.navigateTo）。
 */
export function navigateToMp(path: string): void {
  const mp = getWxMiniProgram();
  if (!mp) return;
  mp.navigateTo({ url: path });
}

/**
 * 重新加载小程序 web-view（通过 wx.miniProgram.reLaunch 回到 splash）。
 */
export function reLaunchMp(path = "/pages/splash/index"): void {
  const mp = getWxMiniProgram();
  if (!mp) return;
  mp.reLaunch({ url: path });
}
